import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Phone } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'

export default function RegisterPage() {
  const { session, loadingAuth, signUp } = useAuth()
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  if (!loadingAuth && session) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (password.length < 6) {
      toast.error('A senha deve ter ao menos 6 caracteres.')
      return
    }
    if (password !== confirmPassword) {
      toast.error('As senhas nao conferem.')
      return
    }

    setSubmitting(true)
    try {
      await signUp({
        fullName: fullName.trim(),
        email: email.trim(),
        password,
      })
      toast.success('Conta criada. Verifique seu e-mail para confirmar o cadastro.')
    } catch (err) {
      toast.error(err?.message || 'Falha ao criar conta.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen w-full min-w-0 bg-background">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl items-center justify-center px-4 py-10">
        <div className="grid w-full max-w-4xl overflow-hidden rounded-3xl border border-border bg-card shadow-lg lg:grid-cols-[1.05fr_1fr]">
          <section className="hidden border-r border-border bg-muted/20 p-8 lg:flex lg:flex-col lg:justify-between">
            <div>
              <div className="flex items-center gap-0">
                <img src="/icon-sm.png" alt="WappDesk" className="h-12 w-12 object-contain" />
                <div>
                  <p className="font-semibold tracking-tight text-foreground">
                    <span className="text-lg">WappDesk</span>{' '}
                    <span className="text-xs font-medium text-muted-foreground">Voice</span>
                  </p>
                  <p className="text-xs text-muted-foreground">Cadastro rapido e seguro</p>
                </div>
              </div>
              <h1 className="mt-8 text-3xl font-semibold leading-tight text-foreground">
                Crie sua conta para comecar a operar voz em minutos.
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">
                Tenha controle de planos, DIDs, filas e creditos com uma experiencia simples para desktop e mobile.
              </p>
            </div>
          </section>

          <section className="p-6 sm:p-8">
            <div className="mb-6 flex items-center justify-center lg:hidden">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-wappdesk-primary/15 text-wappdesk-primary">
                <Phone className="h-8 w-8" aria-hidden />
              </div>
            </div>

            <div className="mb-6 text-center lg:text-left">
              <h2 className="text-2xl font-bold text-foreground">Criar conta</h2>
              <p className="mt-1 text-sm text-muted-foreground">Use os mesmos dados da sua operacao principal.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground">Nome completo</span>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="min-h-[44px] w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground">E-mail</span>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-h-[44px] w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground">Senha</span>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="min-h-[44px] w-full rounded-lg border border-input bg-background px-3 py-2.5 pr-10 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-2 inline-flex items-center text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-foreground">Confirmar senha</span>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="min-h-[44px] w-full rounded-lg border border-input bg-background px-3 py-2.5 pr-10 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute inset-y-0 right-2 inline-flex items-center text-muted-foreground hover:text-foreground"
                    aria-label={showConfirmPassword ? 'Ocultar confirmar senha' : 'Mostrar confirmar senha'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </label>

              <button
                type="submit"
                disabled={submitting || loadingAuth}
                className="min-h-[44px] w-full rounded-lg bg-primary py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50 touch-manipulation"
              >
                {submitting ? 'Criando conta...' : 'Criar conta'}
              </button>
            </form>

            <p className="mt-5 text-center text-sm text-muted-foreground">
              Ja tem conta?{' '}
              <Link to="/login" className="font-medium text-primary hover:underline">
                Entrar
              </Link>
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
